import React, { useState } from 'react';
import { WordsData } from '../types/analytics.types';
import './WordErrorsAnalysis.css';

interface WordErrorsAnalysisProps {
  data: WordsData;
  loading?: boolean;
}

type ViewMode = 'problematic' | 'practiced';

export const WordErrorsAnalysis: React.FC<WordErrorsAnalysisProps> = ({ data, loading }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('problematic');

  if (loading) {
    return (
      <div className="word-errors">
        <h4>Word Analysis</h4>
        <div className="loading-skeleton" style={{ height: '300px' }}></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="word-errors">
        <h4>Word Analysis</h4>
        <p className="no-data">No word data available</p>
      </div>
    );
  }

  const displayData = viewMode === 'problematic'
    ? data.problematic_words || []
    : data.most_practiced_words || [];

  const hasData = displayData.length > 0;

  return (
    <div className="word-errors">
      <div className="errors-header">
        <h4>Word Analysis & Performance</h4>
        <div className="view-mode-selector">
          <button
            className={viewMode === 'problematic' ? 'active' : ''}
            onClick={() => setViewMode('problematic')}
          >
            Problematic Words
          </button>
          <button
            className={viewMode === 'practiced' ? 'active' : ''}
            onClick={() => setViewMode('practiced')}
          >
            Most Practiced
          </button>
        </div>
      </div>

      {!hasData ? (
        <p className="no-data">
          {viewMode === 'problematic'
            ? 'No problematic words identified - great job!'
            : 'No practice data available'}
        </p>
      ) : (
        <>
          <div className="words-grid">
            {displayData.slice(0, 12).map((word, index) => {
              const errorRate = word.error_rate || 0;
              const successRate = 100 - errorRate;

              return (
                <div key={word.word_id} className="word-card">
                  <div className="word-rank">#{index + 1}</div>
                  <div className="word-id">{word.word_id}</div>
                  <div className="word-stats">
                    <div className="stat-row">
                      <span className="stat-label">Attempts:</span>
                      <span className="stat-value">{word.total_attempts.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Users:</span>
                      <span className="stat-value">{word.unique_users.toLocaleString()}</span>
                    </div>
                    {viewMode === 'problematic' ? (
                      <div className="stat-row">
                        <span className="stat-label">Error Rate:</span>
                        <span className={`stat-value error-rate ${
                          errorRate >= 50 ? 'high' : errorRate >= 30 ? 'medium' : 'low'
                        }`}>
                          {errorRate.toFixed(1)}%
                        </span>
                      </div>
                    ) : (
                      <div className="stat-row">
                        <span className="stat-label">Success:</span>
                        <span className={`stat-value success-rate ${
                          successRate >= 80 ? 'high' : successRate >= 60 ? 'medium' : 'low'
                        }`}>
                          {successRate.toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="word-progress">
                    <div className="progress-bar">
                      <div
                        className={`progress-fill ${
                          viewMode === 'problematic' ? 'error' : 'success'
                        }`}
                        style={{
                          width: viewMode === 'problematic'
                            ? `${errorRate}%`
                            : `${successRate}%`
                        }}
                      ></div>
                    </div>
                    <div className="progress-label">
                      {word.correct} correct / {word.incorrect} incorrect
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="word-summary">
            <div className="summary-card">
              <div className="summary-icon">📚</div>
              <div className="summary-content">
                <div className="summary-value">{data.total_unique_words.toLocaleString()}</div>
                <div className="summary-label">Total Unique Words</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon">
                {viewMode === 'problematic' ? '⚠️' : '✨'}
              </div>
              <div className="summary-content">
                <div className="summary-value">{displayData.length}</div>
                <div className="summary-label">
                  {viewMode === 'problematic' ? 'Problematic Words' : 'Most Practiced Words'}
                </div>
              </div>
            </div>
            {viewMode === 'problematic' && displayData.length > 0 && (
              <div className="summary-card">
                <div className="summary-icon">📊</div>
                <div className="summary-content">
                  <div className="summary-value">
                    {(displayData.reduce((sum, w) => sum + w.error_rate, 0) / displayData.length).toFixed(1)}%
                  </div>
                  <div className="summary-label">Avg Error Rate</div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WordErrorsAnalysis;
